import { Translations } from './types';

export const en: Translations = {
  seo: {
    home: {
      title: 'Andres Luna | Executive Career Coach | HSE, ESG & Leadership',
      metaDescription: '1:1 Executive Coaching for professionals across the Americas. Clarity, strategic positioning, and international career growth with Andres Luna.',
      ogTitle: 'Andres Luna — Executive Career Coach',
      ogDescription: '1:1 Executive Coaching for professionals seeking clarity, strategy, and purposeful career growth.',
    },
    coaching: {
      title: '1:1 Executive Career Coaching | Andres Luna',
      metaDescription: 'Personalized executive career coaching. Positioning, leadership, internationalization, and growth strategy for professionals across the Americas.',
      ogTitle: '1:1 Executive Coaching — Andres Luna',
      ogDescription: 'Strategic coaching for professionals ready to advance with clarity and positioning.',
    },
    about: {
      title: 'About Andrés Luna | Executive Coach, HSE, ESG & Leadership',
      metaDescription: 'Meet Andres Luna: Executive Career Coach with 17+ years of international executive experience in HSE, ESG, sustainability, and leadership. Founder of GreenIn.',
      ogTitle: 'About Andrés Luna — Executive Coach & Leadership Strategist',
      ogDescription: 'Executive coach with real-world experience in international leadership, HSE, ESG, and strategic career development.',
    },
    interviews: {
      title: 'Interviews & Media | Andres Luna — Leadership & Growth',
      metaDescription: 'Interviews, podcasts, and media on leadership, professional migration, HSE, ESG, and career internationalization with Andres Luna.',
      ogTitle: 'Interviews & Media — Andres Luna',
      ogDescription: 'Conversations on leadership, professional growth, and strategic migration.',
    },
    talks: {
      title: 'Speaking & Conferences | Andres Luna — International Speaker',
      metaDescription: 'Andres Luna: international speaker on leadership, HSE, ESG, Generative AI, migration, and professional growth for executive audiences.',
      ogTitle: 'Speaking & Conferences — Andres Luna',
      ogDescription: 'International speaker on leadership, professional growth, innovation, and transformation.',
    },
    ecosystem: {
      title: 'GreenIn Ecosystem | Innovation, Impact & Leadership',
      metaDescription: 'The GreenIn Ecosystem connects innovation, social impact, coach development, and professional leadership through GreenIn Company, GreenIn Impact, GreenIn Coach, and Andres Luna Coaching.',
      ogTitle: 'GreenIn Ecosystem — Andres Luna',
      ogDescription: 'Innovation, impact, and leadership connected through a unified growth ecosystem.',
    },
    blog: {
      title: 'Blog | Career Strategy & Professional Leadership | Andres Luna',
      metaDescription: 'Articles on career strategy, leadership, HSE, ESG, Generative AI, professional migration, and executive positioning by Andres Luna.',
      ogTitle: 'Blog — Andres Luna',
      ogDescription: 'Ideas and strategies on professional growth, leadership, and positioning.',
    },
    books: {
      title: 'Books & Publications | Andres Luna — Leadership & Strategy',
      metaDescription: 'Publications and resources on leadership, career strategy, sustainability, migration, and innovation by Andres Luna.',
      ogTitle: 'Books & Publications — Andres Luna',
      ogDescription: 'Resources on leadership, career, and innovation with impact.',
    },
    contact: {
      title: 'Contact | Andres Luna — Executive Career Coaching',
      metaDescription: 'Connect with Andres Luna for executive coaching, speaking, interviews, collaborations, and strategic professional growth.',
      ogTitle: 'Contact — Andres Luna',
      ogDescription: 'Connect with Andres Luna for executive coaching, speaking, and strategic collaborations.',
    },
  },
  nav: {
    inicio: 'Home',
    coaching: 'Coaching Services',
    sobre: 'About Andrés',
    entrevistas: 'Interviews & Media',
    charlas: 'Speaking',
    ecosistema: 'GreenIn Ecosystem',
    blog: 'Blog',
    libros: 'Resources',
    contacto: 'Contact',
    cta: 'Book your session',
  },
  hero: {
    headline: 'Executive coaching with Andrés Luna: clarity, strategy, and positioning for your next professional level.',
    subheadline: '1:1 Executive Coaching for professionals across the Americas who want to grow with direction, international visibility, and real leadership.',
    signature: 'Andres Luna — Executive Career Coach | HSE, ESG & Leadership Strategist | Founder of GreenIn',
    ctaPrimary: 'Book your diagnostic session',
    ctaSecondary: 'Explore services',
    microcopy: 'Your first session is free. We assess your goals, challenges, and the best path forward — together.',
  },
  trust: {
    items: [
      '17+ years of international executive experience',
      'Founder of GreenIn Company',
      'International Speaker',
      'HSE / ESG / Sustainability Executive',
      'Bilingual: English & Spanish',
    ],
  },
  audience: {
    title: 'Who is this coaching for?',
    subtitle: 'For professionals across Latin America, Canada, the United States, and the Americas who need more than experience — they need strategy.',
    cards: [
      { title: 'Engineers & technical professionals', description: 'Professionals who\'ve mastered the technical side and now seek leadership roles, visibility, and executive positioning.' },
      { title: 'HSE / EHS Professionals', description: 'Safety, health, and environmental specialists ready to scale into strategic and international positions.' },
      { title: 'Sustainability & ESG Leaders', description: 'Professionals driving sustainable transformation who need to position themselves as industry leaders.' },
      { title: 'Project Managers & operations leaders', description: 'Project leaders ready to move beyond the operational and grow into executive roles.' },
      { title: 'Professionals in transition', description: 'People reinventing their career, switching industries, or seeking a new direction with purpose.' },
      { title: 'Migrating professionals', description: 'Those relocating to a new country who need a real strategy to build a career — not just find a job.' },
      { title: 'Future leaders', description: 'Ambitious professionals who want to prepare for leadership with the right tools, clarity, and confidence.' },
    ],
  },
  why: {
    title: 'Why take these sessions?',
    statements: [
      "It's not just about finding a job.",
      "It's about building a career that's solid, visible, and internationally relevant.",
      'Experience is valuable. But without direction, it dilutes.',
      'Clarity + positioning + strategy = real growth.',
    ],
  },
  outcomes: {
    title: 'What we work on together',
    items: [
      'Career clarity and professional direction',
      'Career transition and reinvention',
      'Leadership and executive growth',
      'Positioning and personal brand',
      'CV / Resume and LinkedIn profile',
      'Interview preparation and strategic networking',
      'Career internationalization',
      'Professional visibility and authority',
      'Purpose, impact, and the next level',
    ],
  },
  services: {
    title: 'Coaching Services',
    subtitle: 'Strategic coaching backed by real experience. Not generic motivation — a clear process for professionals who want concrete results.',
  },
  ai: {
    title: 'Generative AI, HSE, ESG, and the future of leadership',
    subtitle: 'A strategic perspective where artificial intelligence, safety, sustainability, and the leadership of the future converge.',
    ecosystemIntro: 'Every coaching package includes access to the GreenIn Coaching Ecosystem — a premium multilingual suite of specialized AI coaches designed to help you grow with clarity, credibility, and strategic positioning.',
    ecosystemByPackage: [
      { package: 'Career Growth Session', tools: ['GreenIn Career Coach', 'GreenIn Language Mentor'] },
      { package: 'Career Acceleration Program', tools: ['GreenIn Career Coach', 'GreenIn Language Mentor', 'GreenIn Executive Positioning Coach'] },
      { package: 'Executive Career Strategy', tools: ['GreenIn Career Coach', 'GreenIn Language Mentor', 'GreenIn Executive Positioning Coach', 'GreenIn Founder & Personal Brand Coach'] },
    ],
    points: [
      'Leading through technological transformation',
      'Generative AI as a tool for professional growth',
      'HSE and sustainability with a future-oriented lens',
      'Applied innovation with purpose and results',
      'Smarter, more competitive professional development',
    ],
    tools: {
      title: 'GreenIn Coaching Ecosystem',
      subtitle: 'A premium multilingual ecosystem of specialized AI coaches. Included in every package — activated from your first session.',
      items: [
        { name: 'GreenIn Career Coach', description: 'Professional growth, job search & career strategy. Grow with clarity. Compete with confidence. Advance with purpose.', href: 'https://chatgpt.com/g/g-UeaDaVjv7-greenin-career-coach', logo: 'greenin-career-coach', available: true },
        { name: 'GreenIn Language Mentor', description: 'AI-powered language coach for professionals who need to improve their English or Spanish communication in executive contexts.', href: '#packages', logo: 'greenin-language-mentor', available: false },
        { name: 'GreenIn Executive Positioning Coach', description: 'Executive brand, leadership positioning & senior career strategy. Lead with credibility. Position with distinction.', href: '#packages', logo: 'greenin-executive-positioning-coach', available: false },
        { name: 'GreenIn Founder & Personal Brand Coach', description: 'Personal brand, business positioning & authority growth. Clarify your value. Build your authority. Attract the right opportunities.', href: '#packages', logo: 'greenin-founder-brand-coach', available: false },
      ],
    },
  },
  ecosystem: {
    title: 'The GreenIn Ecosystem',
    subtitle: 'Innovation, impact, and leadership connected through an integrated growth vision.',
    bridge: 'GreenIn builds the ecosystem. Andres Luna brings the voice, the strategy, and the human connection that makes it possible.',
    pillars: [
      { title: 'GreenIn Company', description: 'Applied innovation, strategic consulting, and safe, sustainable growth for organizations.' },
      { title: 'GreenIn Impact', description: 'Social and environmental impact with regional vision, purpose, and circular value.' },
      { title: 'GreenIn Coach', description: 'The GreenIn Company business line dedicated to supporting, certifying, and developing coaches across the Americas with methodology, community, and visibility.' },
      { title: 'Andres Luna Coaching', description: 'Leadership, influence, and professional development with clarity, strategy, and purpose.' },
    ],
  },
  media: {
    title: 'Interviews, media, and conversations',
    subtitle: 'Ideas and perspectives on leadership, professional growth, strategic migration, and the future of work.',
    items: [
      { title: 'Pillars for a Successful Migration', description: 'A 7-episode series on financial planning, emotional intelligence, networking, and key tools for migrating with strategy and purpose.' },
      { title: 'Internationalizing the HSE Career', description: 'An interview on projecting your technical career internationally with strategic vision and multicultural leadership.' },
    ],
  },
  blog: {
    title: 'Blog',
    subtitle: 'Strategy, leadership, and practical ideas for professionals who want to grow with direction, purpose, and visibility.',
    comingSoon: 'Strategic content in development. Stay connected.',
    categories: [
      'Career Strategy',
      'Leadership',
      'HSE / EHS',
      'ESG & Sustainability',
      'Generative AI',
      'Migration & Reinvention',
      'Personal Brand',
      'Newcomer Career Growth',
      'GreenIn Insights',
    ],
    featured: 'Featured article',
    allArticles: 'All articles',
    readMore: 'Read article',
    searchPlaceholder: 'Search articles...',
    relatedArticles: 'Related articles',
    noResults: 'No articles found.',
    allFilter: 'All',
    loadMore: 'View more articles',
    newsletter: {
      title: 'Want ideas that accelerate your career?',
      subtitle: 'Subscribe for strategic content on leadership, positioning, and professional growth.',
      placeholder: 'Your professional email',
      cta: 'Subscribe',
    },
    articleCta: {
      title: 'Ready to move forward with strategy?',
      subtitle: 'Book your diagnostic session and let\'s design your next step together.',
    },
    posts: [
      {
        slug: 'how-to-internationalize-your-career-with-strategy',
        title: 'How to internationalize your career with a realistic strategy',
        excerpt: 'The key steps to project your career internationally with strategic vision — not just operational thinking.',
        category: 'Career Strategy',
        tags: ['internationalization', 'career strategy', 'migration', 'LATAM professionals'],
        date: '2025-03-01',
        updatedAt: '2025-03-05',
        author: 'Andres Luna',
        readingTime: '8 min',
        pillar: 'internationalization',
        relatedSlugs: ['migrate-with-strategy-build-a-career', 'strategic-linkedin-international-career'],
        seoTitle: 'How to internationalize your professional career with strategy | Andres Luna',
        metaDescription: 'Strategic guide to internationalizing your career. Key steps, common mistakes, and how to position yourself in the international market.',
        content: [
          'Internationalizing your career isn\'t simply about looking for a job in another country. It\'s a strategic process that demands clarity about your professional value, your positioning, and your adaptability.',
          'Working with professionals across the Americas, I\'ve identified clear patterns that distinguish those who build solid international careers from those who simply "migrate for a job."',
          '## The 5 Pillars of Professional Internationalization',
          '**1. Clarity of value proposition.** Before projecting yourself, you need to precisely articulate what problem you solve, for whom, and what sets you apart. This is critical for HSE, ESG, and sustainability professionals.',
          '**2. Strategic LinkedIn positioning.** Your LinkedIn profile isn\'t a digital CV — it\'s your global platform for professional authority.',
          '**3. Intentional networking.** International networking requires strategy. Every conversation should create value.',
          '**4. Cultural and communication adaptation.** Understanding cultural differences in international work environments is decisive.',
          '**5. Action plan with a timeline.** An international career is built with measurable steps, not vague aspirations.',
          '## Common mistake: confusing urgency with strategy',
          'The best international transitions come from planning, not improvisation. If you\'re considering this step, I invite you to assess where you are and where you want to go.',
        ],
      },
      {
        slug: 'hse-professionals-growth-leadership',
        title: 'What HSE professionals need to grow toward leadership',
        excerpt: 'The path from HSE specialist to strategic leader requires more than certifications. Discover what makes the difference.',
        category: 'HSE / EHS',
        tags: ['HSE', 'leadership', 'professional growth', 'safety'],
        date: '2025-02-15',
        author: 'Andres Luna',
        readingTime: '7 min',
        pillar: 'hse-growth',
        relatedSlugs: ['from-technical-specialist-to-leader', '7-mistakes-holding-back-technical-professionals'],
        seoTitle: 'Professional growth for HSE: from specialist to leader | Andres Luna',
        metaDescription: 'What HSE/EHS professionals need to grow into strategic leadership roles with greater organizational impact.',
        content: [
          'HSE professionals are pillars of any serious organization. Yet many remain in operational roles without accessing strategic leadership positions.',
          '## The invisible ceiling for HSE professionals',
          'After years of certifications and risk management, many feel they\'ve hit a ceiling. The reality: growth toward leadership requires shifting from "compliance executor" to "organizational value strategist."',
          '**What organizations need from their HSE leaders:**',
          '- Strategic vision connecting safety to business results',
          '- C-suite level influence',
          '- Multicultural team leadership',
          '- Understanding ESG as a value framework',
          '- Executive communication with impact',
          '## The 3 key transitions',
          '**From operational to strategic:** Think about how HSE generates competitive value, not just compliance.',
          '**From technical to communicator:** Present data as organizational impact narrative.',
          '**From local to global:** International experience and multicultural understanding are decisive differentiators.',
        ],
      },
      {
        slug: 'generative-ai-hse-sustainability-future',
        title: 'Generative AI, HSE, and sustainability: opportunities for the professional of the future',
        excerpt: 'Generative AI is transforming safety, sustainability, and leadership. How can you position yourself at this intersection?',
        category: 'Generative AI',
        tags: ['Generative AI', 'HSE', 'sustainability', 'future of work', 'innovation'],
        date: '2025-02-01',
        author: 'Andres Luna',
        readingTime: '9 min',
        pillar: 'ai-future',
        relatedSlugs: ['hse-professionals-growth-leadership', 'esg-sustainability-professional-growth'],
        seoTitle: 'Generative AI, HSE & Sustainability: professional opportunities | Andres Luna',
        metaDescription: 'How Generative AI is creating new opportunities in HSE, sustainability, and leadership for the professional of the future.',
        content: [
          'The intersection of Generative AI, HSE, and sustainability is creating an unprecedented opportunity space for those who know how to position themselves.',
          '## Generative AI: beyond the hype',
          'It\'s not about replacing professionals. It\'s about amplifying capabilities, automating the repetitive, and freeing space for strategic thinking that truly generates value.',
          '**Concrete applications in HSE:**',
          '- Predictive analysis of operational risks',
          '- Automated safety report generation',
          '- Personalized training with simulations',
          '- Real-time environmental data analysis',
          '**Applications in sustainability and ESG:**',
          '- ESG reporting automation',
          '- Sustainable supply chain analysis',
          '- Climate scenario modeling',
          '- Data-driven impact communication',
          '## The professional of the future: technical + strategic + digital',
          'Those who combine technical HSE/ESG knowledge with leadership skills and AI understanding will hold a significant competitive advantage.',
          'The key isn\'t becoming an AI expert — it\'s integrating AI strategically into your professional practice.',
        ],
      },
      {
        slug: '7-mistakes-holding-back-technical-professionals',
        title: '7 mistakes that hold back the professional growth of technical profiles',
        excerpt: 'Technical professionals often make strategic mistakes that limit their growth. Identify and overcome them.',
        category: 'Career Strategy',
        tags: ['professional growth', 'engineers', 'technical profiles', 'career mistakes'],
        date: '2025-01-20',
        author: 'Andres Luna',
        readingTime: '6 min',
        pillar: 'executive-coaching',
        relatedSlugs: ['from-technical-specialist-to-leader', 'strengthen-professional-positioning-changing-market'],
        seoTitle: '7 mistakes holding back your professional growth as a technical profile',
        metaDescription: 'The 7 most common mistakes holding back growth for engineers and technical profiles. Overcome them with strategy.',
        content: [
          'I\'ve worked with hundreds of technical professionals — engineers, HSE specialists, project managers, sustainability leaders — and I\'ve identified systematic patterns that hold back their growth.',
          '## Mistake 1: Believing more certifications = more growth',
          'Certifications are valuable, but they don\'t replace strategic positioning. Real growth comes from combining technical knowledge with visibility and leadership.',
          '## Mistake 2: Not investing in personal brand',
          'Your professional reputation is built actively. If you\'re not visible on LinkedIn, at events, or in relevant industry conversations, you\'re leaving opportunities on the table.',
          '## Mistake 3: Avoiding difficult conversations',
          'Negotiating salary, asking for promotion, giving feedback — essential skills that technical profiles often postpone.',
          '## Mistake 4: Not developing executive communication',
          'Explaining your impact in business language is fundamental. Leaders don\'t just do excellent technical work — they communicate their value.',
          '## Mistake 5: Isolating yourself in your specialty',
          'Building strategic relationships outside your area is key for long-term growth.',
          '## Mistake 6: Not having a 3–5 year career plan',
          'Without direction, any path seems right. The professionals who grow most know where they\'re headed.',
          '## Mistake 7: Expecting merit to speak for itself',
          'Excellent work is necessary but not sufficient. You need visibility, advocacy, and positioning.',
        ],
      },
      {
        slug: 'strengthen-professional-positioning-changing-market',
        title: 'How to strengthen your professional positioning in a changing market',
        excerpt: 'In an evolving market, your positioning is your greatest asset. Learn to strengthen it with strategy.',
        category: 'Personal Brand',
        tags: ['positioning', 'personal brand', 'job market', 'strategy'],
        date: '2025-01-10',
        author: 'Andres Luna',
        readingTime: '7 min',
        pillar: 'executive-coaching',
        relatedSlugs: ['professional-brand-matters-more-than-you-think', 'strategic-linkedin-international-career'],
        seoTitle: 'Strengthen your professional positioning in a changing market',
        metaDescription: 'Strategies to strengthen your professional positioning and stand out in a constantly transforming job market.',
        content: [
          'Today\'s job market doesn\'t just reward experience. It rewards relevance, visibility, and strategic positioning.',
          '## What is professional positioning?',
          'It\'s the market\'s perception of you. It\'s not what you do — it\'s how the world sees you. And that perception can and should be built with intention.',
          '## The 4 pillars of positioning',
          '**1. Clarity of value proposition:** What problem do you solve? For whom? What sets you apart?',
          '**2. Strategic visibility:** Being present where it matters — LinkedIn, events, publications, industry conversations.',
          '**3. Consistency:** Your message, image, and behavior aligned.',
          '**4. Value network:** The professional relationships you build amplify or limit your reach.',
          '## In a changing market, positioning is your anchor',
          'When industries transform and technology advances, your positioning keeps you relevant. Don\'t wait for the market to define you.',
        ],
      },
      {
        slug: 'migrate-with-strategy-build-a-career',
        title: 'Migrate with strategy: beyond employment, build a career',
        excerpt: 'Successful professional migration isn\'t about finding a job. It\'s about building a career with vision and strategy.',
        category: 'Migration & Reinvention',
        tags: ['migration', 'newcomers', 'Canada', 'career strategy', 'LATAM'],
        date: '2024-12-15',
        author: 'Andres Luna',
        readingTime: '8 min',
        pillar: 'internationalization',
        relatedSlugs: ['how-to-internationalize-your-career-with-strategy', 'strategic-linkedin-international-career'],
        seoTitle: 'Migrate with professional strategy: beyond employment | Andres Luna',
        metaDescription: 'Strategic guide for migrating professionals. How to build an international career with purpose.',
        content: [
          'Professional migration is one of the most transformative decisions a professional can make. And one of the most complex without strategy.',
          '## The most common mistake: looking for a job instead of building a career',
          'Many migrating professionals focus on finding quick employment. Understandable, but limiting. The difference between "finding a job" and "building a career" is the difference between surviving and thriving.',
          '## Strategy before urgency',
          'Before updating your CV or applying to hundreds of positions, answer: What\'s your value proposition? How does your experience translate to the new market? What gaps do you need to close?',
          '## The 5 pillars of strategic migration',
          '**1. Deep professional self-awareness** — Understand not just what you know, but the value you generate.',
          '**2. Market research** — Understand what\'s in demand, how hiring works, what\'s valued.',
          '**3. Strategic adaptation** — Your CV, LinkedIn, and narrative must speak the new market\'s language.',
          '**4. Intentional networking** — In countries like Canada, networking isn\'t optional — it\'s fundamental.',
          '**5. Builder\'s mindset** — Think in 2-3 years, not 2-3 months.',
        ],
      },
      {
        slug: 'from-technical-specialist-to-leader',
        title: 'From technical specialist to leader: the change few prepare for',
        excerpt: 'The leap from specialist to leader requires strategic preparation. Discover how to make the transition successfully.',
        category: 'Leadership',
        tags: ['leadership', 'transition', 'technical profiles', 'professional growth'],
        date: '2024-12-01',
        author: 'Andres Luna',
        readingTime: '7 min',
        pillar: 'executive-coaching',
        relatedSlugs: ['7-mistakes-holding-back-technical-professionals', 'hse-professionals-growth-leadership'],
        seoTitle: 'From technical specialist to leader: prepare your transition | Andres Luna',
        metaDescription: 'How to prepare the transition from technical specialist to leader. Strategies, skills, and mindset for the next level.',
        content: [
          'The path from technical specialist to leader is one of the most rewarding — and one of the most underestimated in its complexity.',
          '## Why do so many technical professionals fail as leaders?',
          'Because they assume the skills that made them excellent specialists will make them good leaders. They won\'t.',
          '## The 3 great transformations',
          '**From doing to enabling:** As a specialist, your value was in doing. As a leader, your value is in enabling others.',
          '**From technical knowledge to emotional intelligence:** Understanding people, motivations, conflicts, and team dynamics.',
          '**From individual to systemic:** Thinking about how the entire organizational system works, not just your work.',
          '## How to prepare the transition',
          '- Find mentors who\'ve already made the transition',
          '- Develop executive communication',
          '- Practice delegation actively',
          '- Build your leadership brand before you need it',
          '- Invest in executive coaching as an accelerator',
          'The transition from specialist to leader doesn\'t happen by accident. It\'s designed, prepared, and executed with intention.',
        ],
      },
      {
        slug: 'professional-brand-matters-more-than-you-think',
        title: 'Why your professional brand matters more than you think',
        excerpt: 'Your professional brand is your strategic reputation. It\'s key to your growth — and it\'s built with intention.',
        category: 'Personal Brand',
        tags: ['personal brand', 'LinkedIn', 'positioning', 'professional visibility'],
        date: '2024-11-15',
        author: 'Andres Luna',
        readingTime: '6 min',
        pillar: 'executive-coaching',
        relatedSlugs: ['strengthen-professional-positioning-changing-market', 'strategic-linkedin-international-career'],
        seoTitle: 'Your professional brand matters more than you think | Andres Luna',
        metaDescription: 'Your professional brand is key to your growth. Learn to build it with strategy and intention.',
        content: [
          'Your professional brand isn\'t your logo or your LinkedIn photo. It\'s the world\'s perception of you as a professional. And that perception directly impacts the opportunities that come your way.',
          '## Professional brand = strategic reputation',
          'Every interaction, publication, presentation, and conversation contributes. The question is: are you building it with intention or letting it form by accident?',
          '## The 5 elements of a solid professional brand',
          '**1. Clear narrative:** What\'s your professional story? What problem do you solve? Where are you headed?',
          '**2. Digital presence:** LinkedIn is your main platform. Is it optimized to showcase your value?',
          '**3. Valuable content:** Sharing knowledge positions you as a thought leader.',
          '**4. Strategic network:** The people you associate with say a lot about you.',
          '**5. Consistency:** Coherence between what you say, what you do, and how you present yourself.',
          '## The return on professional branding',
          'Professionals with strong brands receive more offers, get invited to more events, and have greater negotiating power. It\'s not vanity — it\'s strategy.',
        ],
      },
      {
        slug: 'esg-sustainability-professional-growth',
        title: 'ESG and sustainability as a path for professional growth',
        excerpt: 'The ESG world offers unique growth opportunities. Learn how to position yourself in this strategic field.',
        category: 'ESG & Sustainability',
        tags: ['ESG', 'sustainability', 'professional growth', 'leadership'],
        date: '2024-11-01',
        author: 'Andres Luna',
        readingTime: '8 min',
        pillar: 'esg-sustainability',
        relatedSlugs: ['generative-ai-hse-sustainability-future', 'hse-professionals-growth-leadership'],
        seoTitle: 'ESG & sustainability: professional growth opportunities | Andres Luna',
        metaDescription: 'How to leverage the growth of ESG and sustainability to boost your career with strategy.',
        content: [
          'The ESG and sustainability field is in full growth. And with that growth come extraordinary opportunities for those who know how to position themselves.',
          '## Context: ESG as a strategic imperative',
          'It\'s no longer a trend. It\'s a regulatory requirement, a competitive differentiator, and a stakeholder expectation. Organizations need professionals who understand ESG as a value strategy, not just compliance.',
          '## Professional opportunities in ESG',
          '- Corporate sustainability leadership',
          '- ESG consulting for companies in transition',
          '- ESG reporting and metrics management',
          '- Sustainability integration in value chains',
          '- Sustainable innovation and circular economy',
          '## How to position yourself',
          '**1. Connect your experience to ESG:** If you come from HSE, risk management, or engineering, you already have solid foundations.',
          '**2. Develop specific knowledge:** Certifications in GRI, SASB, TCFD complement your profile.',
          '**3. Build a sustainability narrative:** Position your experience in the context of impact and value.',
          '**4. Connect with the ecosystem:** Participate in events, communities, and ESG conversations.',
          'The future of many industries runs through sustainability. Positioning yourself today means investing in tomorrow\'s advantage.',
        ],
      },
      {
        slug: 'strategic-linkedin-international-career',
        title: 'How to use LinkedIn strategically to project your international career',
        excerpt: 'LinkedIn is far more than an online CV. Learn to use it as a strategic tool for international professional projection.',
        category: 'Career Strategy',
        tags: ['LinkedIn', 'internationalization', 'personal brand', 'networking'],
        date: '2024-10-15',
        author: 'Andres Luna',
        readingTime: '7 min',
        pillar: 'executive-coaching',
        relatedSlugs: ['professional-brand-matters-more-than-you-think', 'how-to-internationalize-your-career-with-strategy'],
        seoTitle: 'Strategic LinkedIn for international career | Andres Luna',
        metaDescription: 'Guide to using LinkedIn strategically to project your international career with visibility and purpose.',
        content: [
          'LinkedIn is the most powerful professional platform in the world. But most professionals use it as a static CV, wasting its true potential.',
          '## LinkedIn is not a CV. It\'s an authority platform.',
          'The difference between a mediocre profile and a strategic one is the same difference between being invisible and being a reference in your field.',
          '## The 7 keys to strategic LinkedIn',
          '**1. Headline that sells your value, not your title.**',
          '**2. About section as an impact narrative.**',
          '**3. Results-oriented experience.**',
          '**4. Regular, relevant content.**',
          '**5. Intentional networking.**',
          '**6. Strategic recommendations.**',
          '**7. Bilingual profile for international markets.**',
          'Your LinkedIn profile works for you 24/7. Make sure it\'s telling the right story.',
        ],
      },
    ],
  },
  books: {
    title: 'Recommended Resources',
    subtitle: 'Books, courses, tools, and curated resources to accelerate your professional growth.',
    comingSoon: 'Resources in development. Stay connected for updates.',
    contentTypes: ['Book', 'Publication', 'Guide', 'Interview', 'Podcast', 'Resource'],
    categories: {
      'HSE': 'HSE',
      'ESG': 'ESG',
      'Leadership': 'Leadership',
      'Organizational Development': 'Organizational Development',
      'Applied AI': 'Applied AI',
      'Coaching': 'Coaching',
      'Sustainability': 'Sustainability',
      'Career Strategy': 'Career Strategy',
      'Migration': 'Migration',
    },
    allFilter: 'All',
    featuredSection: 'Featured Reads',
    buyNow: 'Get this book',
    noBooks: 'Books coming soon. Stay connected.',
    noBooksInCategory: 'No books in this category yet.',
  },
  resources: {
    hubTitle: 'Resource Hub',
    hubSubtitle: 'Everything you need to grow: books, courses, templates, and tools — curated for ambitious professionals.',
    tabs: {
      books: 'Books',
      courses: 'Courses',
      library: 'Library',
      training: 'Training',
    },
    searchPlaceholder: 'Search resources...',
    viewCourse: 'View course',
    downloadResource: 'Download',
    noResources: 'Resources coming soon. Stay connected.',
    shopTitle: 'GreenIn Shop',
    shopDescription: 'Execution & Circular Impact — Marketplace for HSE & ESG execution tools with circular impact.',
    shopCta: 'Explore GreenIn Shop',
    professionFilter: 'Profession',
    allProfessions: 'All professions',
  },
  contact: {
    title: "Let's Talk",
    subtitle: "I'm available for executive coaching, speaking, interviews, collaborations, and growth opportunities with real impact.",
    directContact: 'Direct contact',
    form: {
      name: 'Full name',
      email: 'Professional email',
      country: 'Country',
      linkedin: 'LinkedIn (optional)',
      message: 'How can I help you?',
      interest: 'Type of inquiry',
      interests: ['Coaching', 'Custom Package', 'Speaking / Conferences', 'Media / Interviews', 'Collaboration', 'Other'],
      submit: 'Send message',
      success: 'Thank you for your message. I\'ve received it and will respond as soon as possible.',
      successSubtext: 'If you prefer, you can also book a free diagnostic session directly.',
      successBookingCta: 'Book a session',
      helperText: 'Your information will only be used to better understand your needs and respond to your inquiry.',
      privacy: 'Your information is confidential. We don\'t share your data with third parties.',
      errors: {
        nameRequired: 'Please enter your name.',
        emailRequired: 'Please enter your email.',
        emailInvalid: 'Please enter a valid email address.',
        countryRequired: 'Please select your country.',
        interestRequired: 'Please select a type of inquiry.',
        messageRequired: 'Please write your message.',
        fieldRequired: 'This field is required.',
      },
    },
  },
  bookingFunnel: {
    title: 'Book your diagnostic session',
    subtitle: 'Share a few details so I can prepare for our conversation.',
    helperText: 'This information helps me understand your profile and goals before our session.',
    fields: {
      name: 'Full name',
      email: 'Professional email',
      country: 'Country',
      profession: 'Professional area',
      professionOptions: [
        'Engineering',
        'HSE / EHS',
        'ESG / Sustainability',
        'Project Management',
        'Leadership / Management',
        'Other',
      ],
      careerMoment: 'Current career moment',
      careerMomentOptions: [
        'Seeking professional clarity',
        'In career transition',
        'Want to grow into leadership',
        'Want to internationalize my career',
        'Want to strengthen my positioning',
        'Other',
      ],
      mainGoal: 'Main goal',
      mainGoalOptions: [
        'Define my next step',
        'Improve my professional profile',
        'Prepare for leadership',
        'Internationalize my career',
        'Improve interviews / networking',
        'Other',
      ],
      referral: 'How did you find this site? (optional)',
      referralPlaceholder: 'LinkedIn, Google, referral...',
    },
    submit: 'Continue',
    submitting: 'Submitting...',
    thankYou: {
      title: 'Thank you. Your information has been received.',
      subtitle: 'The next step is to book your free diagnostic session on the calendar.',
      calendarCta: 'Go to calendar',
      whatsappCta: 'Message me on WhatsApp',
      backCta: 'Back to site',
      closingLine: 'You\'re one step closer to building a career with greater clarity, strategy, and purpose.',
    },
    errors: {
      nameRequired: 'Please enter your name.',
      emailRequired: 'Please enter your email.',
      emailInvalid: 'Please enter a valid email address.',
      countryRequired: 'Please select your country.',
      professionRequired: 'Please select your professional area.',
      careerMomentRequired: 'Please select your current moment.',
      mainGoalRequired: 'Please select your main goal.',
    },
  },
  speakingInquiry: {
    title: 'Speaking inquiry',
    subtitle: 'Tell me about your event and let\'s explore how to collaborate.',
    helperText: 'Share the basic details so I can evaluate the best fit for your event.',
    fields: {
      name: 'Name',
      organization: 'Organization',
      email: 'Email',
      country: 'Country',
      eventType: 'Event type',
      eventTypeOptions: ['Conference', 'Panel', 'Workshop', 'Podcast / Interview', 'Webinar', 'Executive session', 'Other'],
      estimatedDate: 'Estimated date',
      topic: 'Topic of interest',
      topicOptions: [
        'Leadership with purpose',
        'HSE / ESG as strategic value',
        'Generative AI and the future of work',
        'Professional migration',
        'Professional brand and leadership',
        'Other',
      ],
      message: 'Additional message',
    },
    submit: 'Send inquiry',
    success: 'Thank you for your interest. I\'ve received your inquiry.',
    successSubtext: 'I\'ll respond as soon as possible. You can also reach me directly:',
  },
  whatsapp: {
    tooltip: 'Have a quick question?',
    prefilled: 'Hi Andrés, I\'m interested in your Executive Career Coaching sessions and would like more information.',
  },
  coaching: {
    heroTitle: '1:1 Executive Coaching',
    heroSubtitle: 'A strategic, human, and practical process to move forward with clarity, stronger positioning, and a real action plan.',
    differenceTitle: 'What makes this coaching different?',
    differenceText: 'It\'s grounded in real executive experience, multicultural leadership, deep understanding of the Americas market, and a vision that integrates AI, sustainability, and the future of work. Sessions are designed to leverage AI tools and AI Agents that optimize key tasks: Resume review, Cover Letter, language coaching, and career optimization processes. Not generic motivation — applied strategy with real innovation.',
    audienceTitle: 'Who is it for?',
    whyTitle: 'Why take it?',
    whyText: 'Because professional growth today demands more than experience — it demands clarity of direction, strategic positioning, smart decisions, and visibility with purpose.',
    packagesTitle: 'Packages & Services',
    packages: [
      {
        name: 'Free Diagnostic Session',
        price: 'Free',
        duration: '30 to 45 minutes',
        description: 'We assess your current situation, goals, and define together the best path forward.',
        features: ['Current situation diagnosis', 'Goals and blockers identification', 'Next steps definition'],
        cta: 'Book your session',
        badge: 'Start here',
        idealFor: 'For professionals who want to explore coaching before committing.',
      },
      {
        name: 'Career Growth Session',
        price: 'From USD $70',
        duration: '45 to 60 minutes',
        description: 'Single session focused on a specific challenge: clarity, profile, interviews, or visibility. Includes access to AI tools for career optimization.',
        features: ['Professional clarity', 'AI-powered profile review', 'Interviews & networking', 'Leadership & visibility', 'Access to GreenIn Career Coach (AI)'],
        cta: 'Book session',
        idealFor: 'For solving a specific challenge with strategic focus & applied AI.',
      },
      {
        name: 'Career Acceleration Program',
        price: 'From USD $195',
        duration: '3 sessions',
        description: 'Comprehensive strategy: AI-powered CV/Resume review, LinkedIn optimization, interview preparation, and personalized action plan with AI agents.',
        features: ['AI-powered career strategy', 'CV & LinkedIn optimized with agents', 'Interview preparation', 'AI language coaching', 'Clear action plan'],
        cta: 'Start program',
        idealFor: 'For professionals in transition who need structure and technology.',
      },
      {
        name: 'Executive Career Strategy',
        price: 'From USD $295',
        duration: '5 sessions',
        description: 'Complete executive positioning: leadership strategy, personal branding, transition planning, networking, and accountability — all powered by AI tools and specialized agents.',
        features: ['Executive positioning', 'AI-powered leadership strategy', 'Personal branding', 'Strategic networking', 'AI language coaching', 'Accountability with agents'],
        cta: 'Start strategy',
        badge: 'Most popular',
        idealFor: 'For professionals seeking comprehensive, innovative, long-term growth.',
      },
      {
        name: 'Custom Packages',
        price: 'Custom',
        duration: 'Personalized program',
        description: 'For newcomers, migrant professionals, small teams, specialized mentoring, and internationalization support.',
        features: ['Custom program', 'Newcomers & migrants', 'Teams & mentoring', 'Internationalization'],
        cta: 'Inquire about availability',
        idealFor: 'For specific needs, teams, or situations requiring a tailored approach.',
      },
    ],
    howTitle: 'How it works',
    steps: [
      { title: 'Book your session', description: 'Reserve a spot for your diagnostic session at no cost.' },
      { title: 'We assess goals & opportunities', description: 'We analyze your situation, strengths, and growth areas with clarity.' },
      { title: 'We define the best path', description: 'We design a personalized strategic plan for your career.' },
      { title: 'We start working together', description: 'We move forward toward your goals with structure and accountability.' },
    ],
    testimonialsTitle: 'What clients say',
    testimonials: [
      { quote: 'Working with Andrés gave me the clarity and confidence I needed to land my next executive role in a new country. His strategic approach is unmatched.', name: 'Carolina M.', role: 'HSE Director', country: 'Colombia → Canada' },
      { quote: 'The coaching sessions helped me redefine my career narrative and position myself as a leader, not just a technical expert. Game changer.', name: 'Ricardo T.', role: 'ESG Manager', country: 'Brazil → USA' },
      { quote: 'Andrés combines real-world executive experience with genuine empathy. He doesn\'t just coach — he mentors with purpose and precision.', name: 'María Elena S.', role: 'Operations VP', country: 'Mexico' },
      { quote: 'After years feeling stuck, three sessions were enough to build a clear roadmap. I got promoted within four months.', name: 'Daniel F.', role: 'Project Lead', country: 'Chile → Australia' },
    ],
  },
  about: {
    heroTitle: 'About Andrés Luna',
    heroSubtitle: 'Executive Career Coach, HSE / ESG leader, entrepreneur, speaker, and connector of people, ideas, and opportunities.',
    storyTitle: 'My Story',
    storyText: [
      'With over 17 years of international executive experience in HSE, ESG, sustainability, risk management, leadership, and project management, I\'ve worked across industries like energy, construction, FMCG, and multicultural environments in 6+ countries.',
      'I\'ve led teams, managed complex operations, and built strategies in demanding contexts. That trajectory gave me a conviction: professional growth isn\'t linear, isn\'t just technical, and requires a strategic vision that integrates leadership, positioning, innovation, and purpose.',
      'Today, as founder of GreenIn Company and Executive Career Coach, I help professionals across the Americas grow, reinvent themselves, and internationalize their careers. With strategy, with clarity, and with the confidence that comes from real experience.',
    ],
    mission: 'To help professionals grow, reinvent themselves, and internationalize their career with strategy, clarity, and purpose.',
    vision: 'A more human, more strategic professional development — connected to the future of work, leadership, and innovation with impact.',
    valuesTitle: 'What I bring',
    values: [
      'Real executive experience across multiple industries and countries',
      'Regional and international vision of the professional market',
      'Multicultural leadership and deep cross-cultural understanding',
      'Technical and strategic knowledge of HSE, ESG, and key industries',
      'Coaching with human depth and practical focus',
      'Integration of career, leadership, AI, and sustainability',
    ],
    metrics: [
      { value: '17+', label: 'Years of experience' },
      { value: '6+', label: 'Countries' },
      { value: '2', label: 'Companies founded' },
      { value: '100+', label: 'Professionals impacted' },
    ],
    credentialsTitle: 'Track record & credentials',
    credentials: [
      'Certified Executive Career Coach',
      'Founder of GreenIn Company',
      'Founder of GreenIn Impact',
      'International Speaker',
      'Executive leader in HSE, ESG & Sustainability',
      'Experience in energy, construction & FMCG',
      'Bilingual: English & Spanish',
      'Track record in Canada, Latin America & the Americas',
    ],
    speakingTitle: 'Speaking & Conferences',
    speakingSubtitle: 'Keynotes and workshops on leadership, sustainability, career growth, and the future of work.',
    speakingCta: 'Would you like to invite me to your event?',
  },
  interviews: {
    heroTitle: 'Interviews, media, and conversations',
    heroSubtitle: 'Ideas and perspectives on leadership, professional growth, strategic migration, and the future of work.',
    introText: 'Communicating is also leading. Every conversation is an opportunity to inspire, educate, and connect with professionals who seek to grow with purpose.',
    whyTitle: 'Why do these conversations matter?',
    whyText: 'Because the ideas we share shape the decisions we make. Every interview, podcast, or article is an invitation to think differently about your career, your leadership, and your impact.',
  },
  talks: {
    heroTitle: 'Speaking, conferences & collaborations',
    heroSubtitle: 'Available for conferences, panels, workshops, podcasts, and strategic conversations on leadership, innovation, and professional growth.',
    introText: 'My approach as a speaker combines real executive experience with a strategic vision of leadership, sustainability, and the future of work. Every presentation aims to leave clear, actionable ideas that spark reflection.',
    topicsTitle: 'Available topics',
    topics: [
      { title: 'Leadership with purpose', description: 'How to lead with vision, impact, and authenticity in a fast-changing world.' },
      { title: 'Professional growth & career internationalization', description: 'Strategies for building international careers with clarity, positioning, and purpose.' },
      { title: 'HSE / ESG as strategic value', description: 'How to position safety and sustainability as real competitive advantage.' },
      { title: 'Generative AI and the future of HSE & sustainability', description: 'Concrete AI opportunities applied to safety, sustainability, and leadership.' },
      { title: 'Innovation with impact', description: 'How to generate innovation that combines purpose, technology, and measurable results.' },
      { title: 'Multicultural leadership', description: 'Leading diverse teams in international settings with cultural intelligence and strategic empathy.' },
      { title: 'Professional migration & reinvention', description: 'The strategic path of migration: building a career, not just finding a job.' },
      { title: 'Professional brand & executive positioning', description: 'Building a professional brand that attracts opportunities — not one that chases them.' },
    ],
    ctaTitle: 'Would you like to invite me to your event?',
    ctaSubtitle: 'Available for conferences, panels, workshops, podcasts, and strategic sessions with executive audiences.',
    formatsTitle: 'Available formats',
    formats: ['Keynote', 'Expert panel', 'Interactive workshop', 'Podcast / Interview', 'Webinar', 'Private executive session'],
  },
  footer: {
    tagline: 'Executive Career Coach | HSE, ESG & Leadership Strategist',
    rights: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms & Conditions',
    navigation: 'Navigation',
    more: 'More',
    contact: 'Contact',
    legal: 'Legal',
    adminAccess: 'Admin Panel',
  },
  common: {
    bookSession: 'Book your session',
    exploreServices: 'Explore services',
    viewPackages: 'View packages',
    viewInterviews: 'View interviews',
    inviteToTalk: 'Invite me to speak',
    knowGreenin: 'Explore GreenIn',
    goToBlog: 'Go to Blog',
    viewPublications: 'View publications',
    contactMe: 'Contact me',
    learnMore: 'Learn more',
    visit: 'Visit',
    backToHome: 'Back to home',
    pageNotFound: 'Page not found',
    pageNotFoundText: 'Sorry, the page you\'re looking for doesn\'t exist.',
    breadcrumbHome: 'Home',
    readAlso: 'Read also',
    shareArticle: 'Share article',
  },
};
